// The full change calendar, one entry's detail, and the staff member's own
// subscription (CAL 01, CAL 02).

import { esc } from '../api';
import { formatWhen } from '../notifications';

export interface CalendarEntry {
  id: string;
  reference?: string;
  title: string;
  systems: string[];
  owner: string;
  scheduledStart: string;
  timezone: string;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
  state: 'scheduled' | 'rescheduled' | 'cancelled';
  published: boolean;
  audience: string;
  impact: string;
  downtime: string;
  userAction: string;
  risk: 'low' | 'medium' | 'high';
  riskRationale: string;
  previousStart?: string;
  cancelReason?: string;
  version?: number;
}

export interface OwnSubscription {
  all: boolean;
  systems: string[];
  updatedAt?: string;
}

const STATE_LABEL = { scheduled: 'Scheduled', rescheduled: 'Rescheduled', cancelled: 'Cancelled' } as const;

export function renderEntryFields(e: CalendarEntry): string {
  const rows: Array<[string, string]> = [
    ['Systems', e.systems.join(', ')],
    ['Owner', e.owner],
    ['When', formatWhen(e.scheduledStart, e.timezone)],
    ...(e.previousStart ? ([['Was', formatWhen(e.previousStart, e.timezone)]] as Array<[string, string]>) : []),
    ['State', STATE_LABEL[e.state]],
    ['Who is affected', e.audience],
    ['What changes', e.impact],
    ['Downtime', e.downtime],
    ['What you need to do', e.userAction],
    ['Risk', `${e.risk}: ${e.riskRationale}`],
    ...(e.cancelReason ? ([['Why cancelled', e.cancelReason]] as Array<[string, string]>) : []),
    ['Created', new Date(e.createdAt).toISOString()],
    ['Last updated', `${new Date(e.updatedAt).toISOString()}${e.updatedBy ? ` by ${e.updatedBy}` : ''}`],
  ];
  return `<dl class="fields">${rows.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${esc(v)}</dd>`).join('')}</dl>`;
}

export function renderEntryCard(e: CalendarEntry): string {
  return `<li class="card entry state-${esc(e.state)} risk-${esc(e.risk)}">
  <h2><a href="#/calendar/${esc(e.id)}">${esc(e.title)}</a> <span class="tag">${STATE_LABEL[e.state]}</span>${
    e.published ? '' : ' <span class="tag">Not published</span>'
  }</h2>
  ${e.reference ? `<p class="muted">${esc(e.reference)}</p>` : ''}
  ${renderEntryFields(e)}
</li>`;
}

export function renderSubscriptionForm(systems: string[], own: OwnSubscription | null, status = ''): string {
  const all = own?.all ?? false;
  const chosen = new Set(own?.systems ?? []);
  return `<form class="subscription" data-action="save-subscription" aria-labelledby="sub-title">
  <h2 id="sub-title">Your change subscription</h2>
  <p class="muted">Preview only. Choose what you would be told about. Nothing is sent from this proof of concept.</p>
  <fieldset>
    <legend>Tell me about</legend>
    <label><input type="radio" name="scope" value="all" ${all ? 'checked' : ''}/> All systems</label>
    <label><input type="radio" name="scope" value="some" ${all ? '' : 'checked'}/> Only these systems</label>
    <div class="chips">${systems
      .map((s) => `<label><input type="checkbox" name="systems" value="${esc(s)}" ${chosen.has(s) ? 'checked' : ''}/> ${esc(s)}</label>`)
      .join('')}</div>
  </fieldset>
  <button type="submit">Save my subscription</button>
  <p class="status" role="status">${esc(status)}${own?.updatedAt ? ` Saved preview subscription, last changed ${esc(new Date(own.updatedAt).toISOString())}.` : ''}</p>
</form>`;
}

export function readSubscriptionForm(form: { scope: string; systems: string[] }): { all: boolean; systems: string[] } {
  return form.scope === 'all' ? { all: true, systems: [] } : { all: false, systems: form.systems };
}

export function renderCalendar(entries: CalendarEntry[], systems: string[], filter: string, own: OwnSubscription | null, status = ''): string {
  const shown = filter ? entries.filter((e) => e.systems.includes(filter)) : entries;
  return `
<h1>Change calendar</h1>
<form class="filter" data-action="filter-calendar">
  <label for="system">System</label>
  <select id="system" name="system">
    <option value="">All systems</option>
    ${systems.map((s) => `<option value="${esc(s)}" ${s === filter ? 'selected' : ''}>${esc(s)}</option>`).join('')}
  </select>
  <button type="submit">Filter</button>
</form>
${shown.length ? `<ul class="entries">${shown.map(renderEntryCard).join('')}</ul>` : '<p class="muted">No changes for this system.</p>'}
${renderSubscriptionForm(systems, own, status)}`;
}

export function renderEntry(e: CalendarEntry): string {
  return `<article class="entry state-${esc(e.state)}">
  <h1>${esc(e.title)} <span class="tag">${STATE_LABEL[e.state]}</span></h1>
  ${e.reference ? `<p class="muted">${esc(e.reference)}</p>` : ''}
  ${renderEntryFields(e)}
  <p><a href="#/calendar">Back to the calendar</a></p>
</article>`;
}
